import { useEffect, useRef, useState } from 'react'
import omit from 'lodash/omit'

import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../constants/common'
import { QA_NAME_CONFIRMATION_MINIMIZED_LEAVE } from '../../constants/qa'

import { MINIMIZED_MODALS_MAX_COUNT } from './useMinimizedModals.constants'

export function useMinimizedModals({
  maxCount = MINIMIZED_MODALS_MAX_COUNT,
} = EMPTY_OBJECT) {
  const [entries, setEntries] = useState(EMPTY_OBJECT)
  const [confirmation, setConfirmation] = useState(null)

  const entriesRef = useRef(entries)

  // Scoped `getIsDirty` checkers from hosts that are not minimizable modals
  // (e.g. an open inline reply form). They only participate in
  // `confirmBeforeAction` — no tray entry, not counted toward `maxCount`.
  const dirtyGuardsRef = useRef(EMPTY_OBJECT)

  useEffect(() => {
    entriesRef.current = entries
  }, [entries])

  function register(entry) {
    setEntries(prev => ({ ...prev, [entry.key]: entry }))
  }

  function unregister(key) {
    setEntries(prev => {
      if (!prev[key]) {
        return prev
      }

      return omit(prev, key)
    })
  }

  function clearAll() {
    if (Object.keys(entriesRef.current).length > 0) {
      Object.values(entriesRef.current).forEach(entry =>
        entry?.onForceClose?.()
      )
    }
  }

  function registerDirtyGuard(key, guard) {
    dirtyGuardsRef.current = { ...dirtyGuardsRef.current, [key]: guard }
  }

  function unregisterDirtyGuard(key) {
    dirtyGuardsRef.current = omit(dirtyGuardsRef.current, key)
  }

  function hasEntry(key) {
    return !!entries[key]
  }

  function getEntry(key) {
    return entries[key]
  }

  function canRegister(key) {
    return !!entries[key] || Object.keys(entries).length < maxCount
  }

  function getReplacementCandidate(targetKey) {
    return (
      Object.values(entriesRef.current).find(
        entry => entry?.key !== targetKey
      ) ?? null
    )
  }

  function showConfirmation(nextConfirmation) {
    setConfirmation(nextConfirmation)
  }

  function hideConfirmation() {
    setConfirmation(null)
  }

  // `scope` (optional, single value or a list) restricts the dirty-state check
  // to entries whose host would actually be unmounted by `action` — for
  // example, switching the email preview item should not prompt for a modal
  // opened from elsewhere. Every matching dirty entry gets its own
  // confirmation in sequence; the action runs after the last submit, and
  // cancelling any confirmation aborts the whole chain.
  function confirmBeforeAction(action, { scope } = EMPTY_OBJECT) {
    const scopes = scope?.length > 0 ? [scope].flat() : null

    const dirtyEntries = [
      ...Object.values(entriesRef.current),
      ...Object.values(dirtyGuardsRef.current),
    ].filter(entry => {
      if (scopes && !scopes.includes(entry?.scope)) {
        return false
      }

      return entry?.getIsDirty?.()
    })

    function confirmEntry(index) {
      if (index >= dirtyEntries.length) {
        action?.()

        return
      }

      const { leaveConfirmation } = dirtyEntries[index]

      function handleSubmit(...args) {
        // Cleanup of any modal whose host is unmounted by the action runs
        // through the regular `useEffect` cleanup path; we don't force-close
        // every dirty entry here, since some of them may live outside the
        // subtree being unmounted by `action`.
        leaveConfirmation?.onSubmit?.(...args)
        confirmEntry(index + 1)
      }

      // Same `{ Component, onSubmit }` override contract as `closeConfirmation`
      // in `useMinimizableModal`.
      if (leaveConfirmation?.Component) {
        setConfirmation({
          Component: leaveConfirmation.Component,
          onCancel: EMPTY_FUNCTION,
          onSubmit: handleSubmit,
        })

        return
      }

      setConfirmation({
        description: 'Modal.MinimizedModalDataWillBeLostConfirmationTextKey',
        qaName: QA_NAME_CONFIRMATION_MINIMIZED_LEAVE,
        onCancel: EMPTY_FUNCTION,
        onSubmit: handleSubmit,
      })
    }

    confirmEntry(0)
  }

  return {
    canRegister,
    clearAll,
    confirmation,
    confirmBeforeAction,
    entries,
    entriesValues: Object.values(entries ?? EMPTY_OBJECT),
    getEntry,
    getReplacementCandidate,
    hasEntry,
    hideConfirmation,
    maxCount,
    register,
    registerDirtyGuard,
    showConfirmation,
    unregister,
    unregisterDirtyGuard,
  }
}
